/**
 * A value shown together with where it came from.
 *
 * Forecast surfaces mix two kinds of number that look identical and mean very
 * different things: one the system derived from history, and one a person set
 * because they knew something the system didn't. Reading a screen without being
 * able to tell them apart is how a rep ends up either distrusting good
 * arithmetic or quietly overwriting a colleague's judgement.
 *
 * The encoding, applied the same way everywhere:
 *
 * - **Derived** — muted, dotted underline. This is the calm default, because
 *   most values are derived and a page where everything shouts is a page where
 *   nothing does. The tooltip says how it was computed.
 * - **Rep-set** — the forecast accent colour and a padlock. It reads as *held*:
 *   the nightly calibration will move around it and leave it alone. The tooltip
 *   names who set it, when, and why, and offers the revert.
 *
 * Reverting restores the derived value rather than clearing the field, which
 * mirrors `reset_to_algorithm` on the calibration side — a rep value supersedes
 * the derived one, it does not replace it, and the derived one keeps moving
 * underneath.
 */
import { Lock } from "lucide-react";
import type { ReactNode } from "react";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import type { RepAttribution } from "@shared-core/data/my-pipeline-types";
import { Button } from "../primitives/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../primitives/tooltip";

export function ProvenanceValue({
  value,
  origin,
  derivedExplanation,
  attribution,
  revertLabel = "Use the system figure",
  onRevert,
  onClick,
  className = "",
}: {
  /** The rendered value — already formatted by the caller. */
  value: ReactNode;
  origin: "derived" | "rep";
  /** How the system computed it. Shown on both origins. */
  derivedExplanation: ReactNode;
  /** Who set it and why. Required when origin is "rep". */
  attribution?: RepAttribution | null;
  revertLabel?: string;
  onRevert?: () => void;
  onClick?: () => void;
  className?: string;
}) {
  const isRep = origin === "rep";

  const body = (
    <span
      className={`inline-flex items-center gap-1 ${
        isRep
          ? "font-medium text-primary"
          : "text-muted-foreground underline decoration-dotted underline-offset-2"
      } ${onClick ? "cursor-pointer hover:opacity-80" : "cursor-help"} ${className}`}
    >
      {isRep && <Lock className="size-3 shrink-0" aria-hidden />}
      {value}
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {onClick ? (
          <button type="button" onClick={onClick} className="text-left">
            {body}
          </button>
        ) : (
          body
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1.5">
        {isRep && attribution ? (
          <>
            <p>
              <span className="font-medium">
                Set by {attribution.set_by_name}
              </span>{" "}
              on {formatShortDateOrDash(attribution.set_at)}.
            </p>
            {attribution.reason_code && <p>{attribution.reason_code}</p>}
            {attribution.note && (
              <p className="italic">&ldquo;{attribution.note}&rdquo;</p>
            )}
            <p className="opacity-80">
              Held — the nightly calibration will not move it.{" "}
              {derivedExplanation}
            </p>
            {onRevert && (
              <Button
                size="sm"
                variant="secondary"
                className="mt-1"
                onClick={onRevert}
              >
                {revertLabel}
              </Button>
            )}
          </>
        ) : (
          <p>{derivedExplanation}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/** Legend explaining the encoding, for the top of a surface that uses it. */
export function ProvenanceLegend({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        <span className="underline decoration-dotted underline-offset-2">
          worked out
        </span>{" "}
        from your history
      </span>
      <span className="inline-flex items-center gap-1 text-primary">
        <Lock className="size-3" aria-hidden />
        <span className="font-medium">set by a person</span>
      </span>
    </span>
  );
}
